import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Banner, Button, Field, Input } from '@acs/ui';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@acs/shared';
import { useI18n, type TranslationKey } from '../../i18n/index.js';
import { applyVerifyCode, checkResetCode, confirmNewPassword } from '../../shared/api/auth.js';
import { authErrorKey } from '../../shared/api/authErrors.js';
import { useAuth } from './AuthProvider.js';
import { AuthShell } from './AuthShell.js';
import { parseActionParams } from './actionParams.js';
import { authGate } from './authGate.js';

type State =
  | { step: 'working' }
  | { step: 'verified' }
  | { step: 'reset'; email: string }
  | { step: 'failed'; reason: TranslationKey };

/**
 * Where the links in our account emails land (doc 88 D4):
 * `/auth/action?mode=verifyEmail|resetPassword&oobCode=…&lang=…`.
 *
 * The code is a bearer secret. It is read once, then removed from the address
 * bar before any request is made, so it does not linger in history, a
 * screenshot or a shared tab; Hosting also sends `Referrer-Policy: no-referrer`
 * for `/auth/**`. A ref guards the apply: StrictMode runs effects twice in
 * development, and a one-time code applied twice fails the second time.
 */
export function AuthActionPage() {
  const { t, setLocale } = useI18n();
  const gate = authGate(useAuth());
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useState(() => parseActionParams(location.search));
  const [state, setState] = useState<State>(() =>
    params ? { step: 'working' } : { step: 'failed', reason: 'authError.linkInvalid' },
  );
  const started = useRef(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<{ password?: string; confirm?: string }>({});
  const [busy, setBusy] = useState(false);

  useLayoutEffect(() => {
    if (location.search) navigate('/auth/action', { replace: true });
    // Mount only: just the first render's URL carries the code.
  }, []);

  useEffect(() => {
    if (!params || started.current) return;
    started.current = true;
    if (params.lang) setLocale(params.lang);
    const run =
      params.mode === 'verifyEmail'
        ? applyVerifyCode(params.code).then(() => setState({ step: 'verified' }))
        : checkResetCode(params.code).then((email) => setState({ step: 'reset', email }));
    run.catch((e: unknown) => setState({ step: 'failed', reason: authErrorKey(e) }));
  }, [params, setLocale]);

  const onReset = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!params) return;
    const problems: typeof fieldError = {};
    if (password.length < PASSWORD_MIN_LENGTH) problems.password = t('auth.passwordTooShort', { min: PASSWORD_MIN_LENGTH });
    else if (password.length > PASSWORD_MAX_LENGTH) problems.password = t('auth.passwordTooLong', { max: PASSWORD_MAX_LENGTH });
    if (confirm !== password) problems.confirm = t('auth.passwordMismatch');
    setFieldError(problems);
    if (problems.password || problems.confirm) return;
    setBusy(true);
    try {
      await confirmNewPassword(params.code, password);
      navigate('/signin', { replace: true, state: { notice: 'passwordChanged' } });
    } catch (e) {
      setState({ step: 'failed', reason: authErrorKey(e) });
    } finally {
      setBusy(false);
    }
  };

  switch (state.step) {
    case 'working':
      return (
        <AuthShell title={t(params?.mode === 'resetPassword' ? 'action.resetTitle' : 'action.verifying')}>
          <Button size="lg" block loading disabled>
            {t('action.continue')}
          </Button>
        </AuthShell>
      );

    case 'verified':
      return (
        <AuthShell
          title={t('action.verified')}
          lede={gate === 'signed_out' ? t('action.verifiedSignIn') : undefined}
        >
          <Button size="lg" block onClick={() => navigate(gate === 'signed_out' ? '/signin' : '/', { replace: true })}>
            {t(gate === 'signed_out' ? 'auth.signInSubmit' : 'action.continue')}
          </Button>
        </AuthShell>
      );

    case 'reset':
      return (
        <AuthShell title={t('action.resetTitle')} lede={t('action.resetFor', { email: state.email })}>
          <form className="auth__fields" onSubmit={(ev) => void onReset(ev)} noValidate>
            {/* Lets password managers file the new password under the right account. */}
            <input type="email" autoComplete="username" value={state.email} readOnly hidden />
            <Field
              label={t('auth.password')}
              hint={t('auth.passwordHint', { min: PASSWORD_MIN_LENGTH })}
              error={fieldError.password}
            >
              <Input
                type="password"
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
              />
            </Field>
            <Field label={t('auth.passwordConfirm')} error={fieldError.confirm}>
              <Input
                type="password"
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
              />
            </Field>
            <Button type="submit" size="lg" block loading={busy}>
              {t('action.resetSubmit')}
            </Button>
          </form>
        </AuthShell>
      );

    case 'failed':
      return (
        <AuthShell title={t('action.invalidTitle')}>
          <Banner tone="danger">{t(state.reason)}</Banner>
          <div className="auth__links auth__links--center">
            <Link className="auth__link" to="/signin">
              {t('forgot.back')}
            </Link>
          </div>
        </AuthShell>
      );
  }
}
